import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[appCardRail]',
})
export class CardRailDirective implements AfterViewInit, OnDestroy {
  private pointerId: number | null = null;
  private startX = 0;
  private startScroll = 0;
  private dragged = false;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('click', this.onClickCapture, true);
  }

  ngOnDestroy(): void {
    const el = this.host.nativeElement;
    el.removeEventListener('wheel', this.onWheel);
    el.removeEventListener('click', this.onClickCapture, true);
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch' || event.button !== 0) {
      return;
    }

    const el = this.host.nativeElement;
    if (el.scrollWidth <= el.clientWidth + 1) {
      return;
    }

    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startScroll = el.scrollLeft;
    this.dragged = false;
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) {
      return;
    }

    const el = this.host.nativeElement;
    const dx = event.clientX - this.startX;
    if (!this.dragged && Math.abs(dx) > 6) {
      this.dragged = true;
      el.classList.add('is-dragging');
      el.style.scrollSnapType = 'none';
      el.setPointerCapture(event.pointerId);
    }

    if (this.dragged) {
      el.scrollLeft = this.startScroll - dx;
    }
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerEnd(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) {
      return;
    }

    this.pointerId = null;
    const el = this.host.nativeElement;
    el.classList.remove('is-dragging');
    el.style.scrollSnapType = '';
  }

  private readonly onWheel = (event: WheelEvent): void => {
    const el = this.host.nativeElement;
    if (el.scrollWidth <= el.clientWidth + 1) {
      return;
    }

    const delta =
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
    if (!delta) {
      return;
    }

    const max = el.scrollWidth - el.clientWidth;
    const next = Math.min(max, Math.max(0, el.scrollLeft + delta));
    if (next === el.scrollLeft) {
      return;
    }

    el.scrollLeft = next;
    event.preventDefault();
  };

  private readonly onClickCapture = (event: MouseEvent): void => {
    if (!this.dragged) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.dragged = false;
  };
}
